import fs from 'fs';
import pathLib from 'path';
import childProcess from 'child_process';
import async from 'async';
// @ts-ignore
import Batch from 'batch';
import { transformErrorForSerialization } from '@/common/tools';
//
const isWin = process.platform === 'win32';
/** 本地列出文件数量上限 100w，超过报错 */
const FILE_LIMIT = 1000000;
/** 列文件时调用 fs.lstat 的并行数 */
const BATCH_LIMIT = 16;

/** listFolder 的回调方法 */
type Callback = (err: NodeJS.ErrnoException | null, fileList?: FileStats[]) => void;

/**
 * 合并目录和文件名，是目录
 * @param dirPath
 * @param fileName
 * @param isDir
 */
const pathJoin = function (dirPath: string, fileName: string, isDir = false) {
  const sep = dirPath.endsWith('/') ? '' : '/';
  let p = dirPath;
  if (fileName) {
    p += sep + fileName;
  }
  isWin && (p = p.replace(/\\/g, '/'));
  // isDir && fileName && (p += '/');
  return p;
};

/**
 *
 * @param dirPath
 * @param callback
 */
const readdir = function stat(
  { dirPath = '', STAT_OPTIONS = {}, relativePath = '', folderName = '' },
  callback: Callback,
) {
  if (!dirPath || !callback) return callback(new Error('ListFolderParamsInvalid'));
  fs.readdir(dirPath, function (err, fileKeys) {
    if (err) return callback(err);
    const batch = new Batch();
    batch.concurrency(BATCH_LIMIT);
    fileKeys.forEach(function (fileKey) {
      const filePath = pathJoin(dirPath, fileKey);
      batch.push((done: (err: NodeJS.ErrnoException | null, stats: FileStats) => void) => {
        fs.lstat(filePath, STAT_OPTIONS, (err, fsStats) => {
          done(
            err,
            statFormat({
              stat: fsStats,
              filePath,
              fileName: fileKey,
              folderName,
              relativePath,
            }),
          );
        });
      });
    });
    batch.end(callback);
  });
};

/** 自定义的文件信息，比 fs.stat、fs.lstat 的信息精简、减少对象/方法引用，达到降低内存占用的目的 */
export interface FileStats {
  /** 文件或文件夹的本地完整路径，路径的分隔符固定用 / 字符 */
  path: string;
  /** 文件或文件夹名称 */
  name: string;
  /** 文件或文件夹大小，文件夹固定是 0，单位 B */
  size: number | bigint;
  /** 文件所在的设备 id */
  dev: number | bigint;
  /** 文件系统特定的文件“Inode”编号。 */
  ino: number | bigint;
  /** 修改时间戳，单位 ms 毫秒 */
  mtimeMs: number | bigint;
  /** 是否文件夹 */
  isDir: boolean;
  /** 是否符号链接 */
  isAlias: boolean;
  /** 文件夹名称 */
  folder: string;
  /** 文件夹总大小 */
  folderSize: number;
  /** 添加上层目录路径，如/Users/A/Downloads/favortest/favor,上传favor目录时，为/Users/A/Downloads/favortest */
  relativePath: string;
}

interface ListFolderOptions {
  /** 要遍历的本地文件夹路径 */
  path: string;
  /** 外部可以传入 taskId，可用于取消任务 */
  taskId?: string;
  statOptions?: Record<string, any>;
  maxFiles?: number;
}

interface ListFoldersOptions {
  /** 要遍历的本地文件夹路径 */
  paths: string[];
  /** 外部可以传入 taskId，可用于取消任务 */
  taskId?: string;
  maxFiles?: number;
}

interface StatFormatParams {
  stat: fs.Stats | fs.BigIntStats;
  filePath: string;
  fileName: string;
  folderName?: string;
  relativePath?: string;
}

// 判断 Mac 中的替身文件/文件夹
export function isFinderAlias(path: string) {
  let isAlias = false;
  try {
    const contentType = childProcess.execFileSync('mdls', ['-raw', '-name', 'kMDItemContentType', path], {
      encoding: 'utf8',
    });
    isAlias = contentType === 'com.apple.alias-file';
  } catch (error) {}
  return isAlias;
}

export const statFormat = function (opt: StatFormatParams) {
  const { stat, filePath, fileName, folderName, relativePath } = opt;
  const isDir = stat.isDirectory();
  const isAlias = stat.isSymbolicLink();
  const item: FileStats = {
    path: filePath,
    name: fileName,
    size: isDir ? 0 : stat.size,
    dev: stat.dev,
    ino: stat.ino,
    mtimeMs: stat.mtimeMs,
    isDir,
    isAlias,
    folder: folderName || '',
    folderSize: 0,
    relativePath: relativePath || '',
  };
  return item;
};

const workingTasks: any = {};

/**
 * 深度遍历本地文件夹
 * @param options
 * @param callback
 */
export const listFolder = function (options: ListFolderOptions, callback: Callback) {
  let opt = options;
  if (opt.taskId) workingTasks[opt.taskId] = true;
  if (typeof opt !== 'object') opt = { path: opt };
  const rootPath = opt.path;
  const MAX_FILES = opt.maxFiles ?? FILE_LIMIT;
  let folderSize = 0;
  let list: FileStats[] = [];
  const STAT_OPTIONS = opt.statOptions || {};
  const rootFolderName = rootPath.replace(/^.*(\/|\\)/, '');
  const relativePath = rootPath.replace(/\/|\\/g, '/').split('/').slice(0, -1).join('/');

  // 列完文件后回调列表数据
  const cb = function (err?: Error | null) {
    if (opt.taskId) delete workingTasks[opt.taskId];
    if (err) {
      callback(err);
    } else {
      list[0].folderSize = Number(folderSize);
      callback(null, list);
    }
  };
  // 深度遍历
  const deep = function (dirStat: FileStats, deepNext: (err?: Error | null) => void) {
    if (opt.taskId && !workingTasks[opt.taskId]) {
      deepNext(new Error('ListFolderSyncCanceled'));
      return;
    }
    logger.worker.info(`[listFolder] deep folder ${dirStat.path} list.length=${list.length}`);
    // 1. 当前目录加入列表
    list.push(dirStat);
    readdir(
      {
        dirPath: dirStat.path,
        STAT_OPTIONS,
        relativePath,
        folderName: rootFolderName,
      },
      function (err, fileStatList) {
        if (err) return deepNext();
        const dirList = fileStatList ? fileStatList.filter(file => file.isDir) : [];
        const fileList = fileStatList ? fileStatList.filter(file => !file.isDir) : [];
        fileList.forEach(file => {
          folderSize += Number(file.size);
        });
        // 2. 子文件加入列表
        list = list.concat(fileList);
        logger.worker.info(
          `[listFolder] concat list.length=${list.length} fileList.length=${fileList.length} dirList.length=${dirList.length}`,
        );
        if (list.length > MAX_FILES) {
          deepNext(new Error('MaxFileLimitReached'));
          return;
        }
        // 3. 对子文件夹深度遍历，注意：deepNext 必须执行一次，返回上一层遍历
        async.eachLimit(dirList, 1, deep, deepNext);
      },
    );
  };

  // 获取根路径的文件夹状态
  fs.lstat(rootPath, STAT_OPTIONS, function (err, fsStats) {
    if (err) return cb(err);
    const filePath = pathJoin(rootPath, '', true);
    const fileName = pathLib.basename(rootPath);
    const rootStat = statFormat({
      stat: fsStats,
      filePath,
      fileName,
      folderName: fsStats.isDirectory() ? rootFolderName : '',
      relativePath,
    });
    deep(rootStat, cb);
  });
};

const listFolderPromise = function (options: ListFolderOptions) {
  return new Promise((resolve, reject) => {
    listFolder(options, function (err, list) {
      if (err) {
        reject(err);
      } else {
        resolve(list);
      }
    });
  });
};

export const listFolders = async function (options: ListFoldersOptions) {
  const filePaths = options.paths;
  let list: FileStats[] = [];
  const errorList: MsgError[] = [];
  try {
    await async.eachLimit(filePaths, 1, (item, next) => {
      listFolderPromise({ ...options, path: item })
        .then(data => {
          list = list.concat(data as FileStats[]);
          next();
        })
        .catch(err => {
          errorList.push(err);
          next(err);
        });
    });
    return { list, errorList };
  } catch (err: MsgError) {
    const error = transformErrorForSerialization(err);
    logger.ui('[listFolder]', error);
    throw error;
  }
};

// 有需要可以提供方法，给外部取消列文件任务
export const cancelListTask = function (taskId: string) {
  delete workingTasks[taskId];
};
